import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Crosshair, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [metaConnected, setMetaConnected] = useState(false);
  const [metaToken, setMetaToken] = useState("");
  const [metaAccountId, setMetaAccountId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    checkMetaConnection();
  }, [clientId]);

  const checkMetaConnection = async () => {
    try {
      const res = await axios.get(`${API}/meta/${clientId}`);
      setMetaConnected(res.data.connected);
    } catch (error) {
      console.error("Erro ao verificar conexão Meta:", error);
    }
  };

  const connectMeta = async () => {
    if (!metaToken) {
      toast.error("Informe o token de acesso da Meta");
      return;
    }

    setSaving(true);
    
    try {
      await axios.post(`${API}/meta/connect/${clientId}`, {
        access_token: metaToken,
        ad_account_id: metaAccountId || null
      });
      
      toast.success("Meta Ads conectada com sucesso!");
      setMetaConnected(true);
    } catch (error) {
      console.error("Erro ao conectar Meta:", error);
      toast.error("Erro ao conectar Meta Ads");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-red-900/10 via-transparent to-transparent"></div>
      
      <div className="relative z-10 container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white"
            data-testid="btn-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div className="flex items-center space-x-3">
            <Crosshair className="w-8 h-8 text-red-600" />
            <span className="text-2xl font-bold">CONFIGURAÇÕES</span>
          </div>
          <div className="w-20"></div>
        </div>

        <div className="max-w-2xl mx-auto space-y-6">
          {/* Meta Ads Connection */}
          <Card className="bg-[#1a1a1a] border-gray-800" data-testid="meta-connection-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Conexão Meta Ads</CardTitle>
                  <CardDescription>
                    Conecte sua conta Meta para publicar campanhas
                  </CardDescription>
                </div>
                
                {metaConnected ? (
                  <CheckCircle2 className="w-6 h-6 text-green-500" data-testid="meta-connected-icon" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-yellow-500" data-testid="meta-disconnected-icon" />
                )}
              </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {metaConnected ? (
                <div className="bg-green-900/20 border border-green-600/30 rounded-lg p-4" data-testid="meta-connected">
                  <p className="text-green-500 font-semibold">✓ Meta Ads conectada</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Suas campanhas podem ser publicadas automaticamente
                  </p>
                </div>
              ) : (
                <div>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="meta-token">Token de Acesso Meta</Label>
                      <Input
                        id="meta-token"
                        data-testid="input-meta-token"
                        placeholder="EAAxxxxxxxxxxxxx"
                        value={metaToken}
                        onChange={(e) => setMetaToken(e.target.value)}
                        className="mt-2"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Obtenha em: Meta Business Settings → System Users
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="meta-account">ID da Conta de Anúncios (opcional)</Label>
                      <Input
                        id="meta-account"
                        data-testid="input-meta-account"
                        placeholder="act_1234567890"
                        value={metaAccountId}
                        onChange={(e) => setMetaAccountId(e.target.value)}
                        className="mt-2"
                      />
                    </div>
                  </div>
                  
                  <Button
                    onClick={connectMeta}
                    disabled={saving}
                    className="bg-red-600 hover:bg-red-700 mt-4"
                    data-testid="btn-connect-meta"
                  >
                    {saving ? "Conectando..." : "Conectar Meta Ads"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Foursquare Info */}
          <Card className="bg-[#1a1a1a] border-gray-800" data-testid="foursquare-info-card">
            <CardHeader>
              <CardTitle>API Foursquare</CardTitle>
              <CardDescription>
                Informações sobre a integração com Foursquare Places
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-4">
                <p className="text-sm text-gray-300">
                  A busca de locais está funcionando em <span className="font-semibold text-blue-500">modo demonstração</span>.
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  Para conectar sua própria API Foursquare:
                </p>
                <ol className="text-sm text-gray-400 mt-2 space-y-1 list-decimal list-inside">
                  <li>Acesse: <a href="https://foursquare.com/developers/signup" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">foursquare.com/developers</a></li>
                  <li>Crie uma conta gratuita</li>
                  <li>Obtenha sua API Key</li>
                  <li>Configure no backend do sistema</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Settings;
